import type { UserRole, Permission } from '../types/auth';

/**
 * Valida y convierte un string a un UserRole válido
 * @param roleName - El nombre del rol a validar
 * @returns Un UserRole válido o 'user' por defecto (SEGURO)
 */
export const validateUserRole = (roleName: string | undefined): UserRole => {
  // Si no hay rol o está vacío, usar 'user' por defecto (SEGURO)
  if (!roleName || roleName.trim() === '') {
    return 'user';
  }
  
  const validRoles: UserRole[] = ['superadmin', 'admin', 'user'];
  const normalizedRole = roleName.toLowerCase().trim();
  
  // Verificar si el rol está en la lista válida
  const foundRole = validRoles.find(role => role === normalizedRole);
  if (foundRole) {
    return foundRole;
  }
  
  // Si no se encuentra, usar 'user' por defecto (SEGURO)
  return 'user';
};

/**
 * Verifica si un string es un UserRole válido
 * @param roleName - El nombre del rol a verificar
 * @returns true si es válido, false si no
 */
export const isValidUserRole = (roleName: string): roleName is UserRole => {
  const validRoles: UserRole[] = ['superadmin', 'admin', 'user'];
  return validRoles.includes(roleName as UserRole);
};

/**
 * Verifica si un string es un Permission válido
 * @param permissionName - El nombre del permiso a verificar
 * @returns true si es válido, false si no
 */
export const isValidPermission = (permissionName: string): permissionName is Permission => {
  const validPermissions: Permission[] = [
    'dashboard.view',
    'leads.view', 'leads.create', 'leads.edit', 'leads.delete',
    'deals.view', 'deals.create', 'deals.edit', 'deals.delete',
    'contacts.view', 'contacts.create', 'contacts.edit', 'contacts.delete',
    'users.view', 'users.create', 'users.edit', 'users.delete',
    'funcionarios.view', 'funcionarios.create', 'funcionarios.edit', 'funcionarios.delete',
    'eventos.view', 'eventos.create', 'eventos.edit', 'eventos.delete',
    'accesos.view', 'accesos.create', 'accesos.edit', 'accesos.delete',
    'reports.view', 'reports.export',
    'configuracion.view', 'configuracion.edit',
    'callcenter.view', 'callcenter.create', 'callcenter.edit',
    'puertas.view', 'puertas.control'
  ];
  return validPermissions.includes(permissionName as Permission);
};

/**
 * Filtra y valida un array de permisos
 * @param permissions - Array de strings de permisos
 * @returns Array de Permission válidos
 */
export const validatePermissions = (permissions: string[]): Permission[] => {
  return permissions.filter(isValidPermission);
};

/**
 * Obtiene el rol por defecto para usuarios sin rol asignado
 * @returns UserRole por defecto (SEGURO)
 */
export const getDefaultRole = (): UserRole => 'user';

/**
 * Obtiene los permisos por defecto para un rol
 * @param role - El rol del usuario
 * @returns Array de permisos por defecto
 */
export const getDefaultPermissionsForRole = (role: UserRole): Permission[] => {
  const defaultPermissions: Record<UserRole, Permission[]> = {
    superadmin: [
      'dashboard.view',
      'leads.view', 'leads.create', 'leads.edit', 'leads.delete',
      'deals.view', 'deals.create', 'deals.edit', 'deals.delete',
      'contacts.view', 'contacts.create', 'contacts.edit', 'contacts.delete',
      'users.view', 'users.create', 'users.edit', 'users.delete',
      'funcionarios.view', 'funcionarios.create', 'funcionarios.edit', 'funcionarios.delete',
      'eventos.view', 'eventos.create', 'eventos.edit', 'eventos.delete',
      'accesos.view', 'accesos.create', 'accesos.edit', 'accesos.delete',
      'reports.view', 'reports.export',
      'configuracion.view', 'configuracion.edit',
      'callcenter.view', 'callcenter.create', 'callcenter.edit',
      'puertas.view', 'puertas.control'
    ],
    admin: [
      'dashboard.view',
      'leads.view', 'leads.create', 'leads.edit', 'leads.delete',
      'deals.view', 'deals.create', 'deals.edit', 'deals.delete',
      'contacts.view', 'contacts.create', 'contacts.edit', 'contacts.delete',
      'users.view', 'users.create', 'users.edit',
      'funcionarios.view', 'funcionarios.create', 'funcionarios.edit', 'funcionarios.delete',
      'eventos.view', 'eventos.create', 'eventos.edit', 'eventos.delete',
      'accesos.view', 'accesos.create', 'accesos.edit', 'accesos.delete',
      'reports.view', 'reports.export',
      'configuracion.view',
      'callcenter.view', 'callcenter.create', 'callcenter.edit',
      'puertas.view', 'puertas.control'
    ],
    // manager: [
    //   'dashboard.view',
    //   'leads.view', 'leads.create', 'leads.edit',
    //   'deals.view', 'deals.create', 'deals.edit',
    //   'contacts.view', 'contacts.create', 'contacts.edit',
    //   'users.view',
    //   'funcionarios.view', 'funcionarios.create', 'funcionarios.edit',
    //   'eventos.view', 'eventos.create', 'eventos.edit',
    //   'accesos.view', 'accesos.create', 'accesos.edit',
    //   'reports.view', 'reports.export',
    //   'callcenter.view', 'callcenter.create', 'callcenter.edit',
    //   'puertas.view'
    // ],
    user: [
      'dashboard.view',
      'leads.view', 'leads.create', 'leads.edit',
      'deals.view', 'deals.create', 'deals.edit',
      'contacts.view', 'contacts.create', 'contacts.edit',
      'eventos.view', 'eventos.create',
      'callcenter.view', 'callcenter.create', 'callcenter.edit'
    ],
    // viewer: [
    //   'dashboard.view',
    //   'leads.view',
    //   'deals.view',
    //   'contacts.view',
    //   'funcionarios.view',
    //   'eventos.view',
    //   'accesos.view',
    //   'reports.view'
    // ],
    // guest: [
    //   'dashboard.view'
    // ]
  };

  return defaultPermissions[role] || [];
};

/**
 * Compara dos roles y determina si el primer rol tiene mayor jerarquía
 * @param role1 - El rol a comparar
 * @param role2 - El rol de referencia
 * @returns true si role1 tiene mayor o igual jerarquía que role2
 */
export const hasHigherOrEqualRole = (role1: UserRole, role2: UserRole): boolean => {
  const roleHierarchy: Record<UserRole, number> = {
    superadmin: 6,
    admin: 5,
    user: 4,
    // agent: 3,
    // viewer: 2,
    // guest: 1
  };

  return roleHierarchy[role1] >= roleHierarchy[role2];
};

/**
 * Obtiene todos los roles disponibles
 * @returns Array de todos los UserRole disponibles
 */
export const getAllRoles = (): UserRole[] => {
  return ['superadmin', 'admin', 'user'];
};

/**
 * Obtiene el nombre legible de un rol
 * @param role - El rol del usuario
 * @returns Nombre legible del rol
 */
export const getRoleDisplayName = (role: UserRole): string => {
  const displayNames: Record<UserRole, string> = {
    superadmin: 'Super Administrador',
    admin: 'Administrador',
    // manager: 'Gerente',
    user: 'Agente',
    // viewer: 'Visualizador',
    // guest: 'Invitado'
  };

  return displayNames[role] || 'Usuario';
};

/**
 * Obtiene la descripción de un rol
 * @param role - El rol del usuario
 * @returns Descripción del rol
 */
export const getRoleDescription = (role: UserRole): string => {
  const descriptions: Record<UserRole, string> = {
    superadmin: 'Acceso completo a todas las funcionalidades del sistema',
    admin: 'Administrador con acceso a la mayoría de funcionalidades',
    // manager: 'Gerente con acceso a gestión de equipos y reportes',
    // agent: 'Agente con acceso a gestión de leads y contactos',
    // viewer: 'Solo acceso de lectura a la mayoría de datos',
    user: 'Acceso muy limitado'
  };

  return descriptions[role] || 'Usuario sin rol definido';
};
